import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { Observable, of } from "rxjs";
import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class ValidatorService {
  public isFieldRequired(formField: any) {
    const required = formField.required || formField.properties.required;
    return required && required.toString().toLowerCase() === "true";
  }

  public isConditionallyRequired(formField: any) {
    return formField.required && formField.required.toString().includes(":");
  }

  public getFieldAccessor(fieldAccessors: any, formFieldID: string) {
    return fieldAccessors[formFieldID];
  }

  public createCrossFieldValidator(
    condition: any,
    target: string,
    conditional: string,
    errorKey: string,
    errorDetails: any = "error",
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const targetControl = group.get(target);
      const conditionalControl = group.get(conditional);
      if (condition(targetControl, conditionalControl)) {
        targetControl?.setErrors({ [errorKey]: errorDetails });
      } else {
        targetControl?.setErrors(null);
      }
      return group.errors;
    };
  }

  public getFieldValidators(formField: any) {
    const validators = [];
    if (this.isFieldRequired(formField)) {
      validators.push(Validators.required);
    }
    if (formField.properties?.maxlength || formField?.dbMaxLength) {
      const dbMaxLength = Number.parseInt(formField?.dbMaxLength, 10);
      const maxlength = Number.parseInt(formField.properties?.maxlength, 10);
      if (!Number.isNaN(maxlength) || !Number.isNaN(dbMaxLength)) {
        const lesserMaxLength = Math.min(
          Number.isNaN(dbMaxLength) ? Number.MAX_SAFE_INTEGER : dbMaxLength,
          Number.isNaN(maxlength) ? Number.MAX_SAFE_INTEGER : maxlength,
        );
        validators.push(Validators.maxLength(lesserMaxLength));
      }
    }
    if (formField.properties?.min) {
      const min = Number.parseInt(formField.properties?.min, 10);
      if (!Number.isNaN(min)) {
        validators.push(Validators.min(min));
      }
    }
    if (formField.properties?.max) {
      const max = Number.parseInt(formField.properties?.max, 10);
      if (!Number.isNaN(max)) {
        validators.push(Validators.max(max));
      }
    }
    if (formField.properties?.pattern) {
      validators.push(Validators.pattern(formField.properties?.pattern));
    }
    return validators;
  }

  public getFormValidators(formField: any, fieldAccessors?: any) {
    const validators = [];
    if (this.isConditionallyRequired(formField)) {
      const [fieldToCheck, valueToCheck] = formField.required.split(":");
      const fieldAccessor = this.getFieldAccessor(fieldAccessors, fieldToCheck);
      const condition = (target: FormControl, conditional: FormControl) => {
        const conditionalCheck = fieldAccessor
          ? fieldAccessor.accessor(conditional?.value) === valueToCheck
          : conditional.value === valueToCheck;
        return conditionalCheck && !target.value;
      };
      validators.push(
        this.createCrossFieldValidator(
          condition,
          formField.properties.id,
          fieldAccessor.id,
          "required",
          true,
        ),
      );
    }
    return validators;
  }

  public createSyncValidator(
    condition: any,
    errorKey: string,
    errorDetails: any = "error",
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (condition(control)) {
        return { [errorKey]: errorDetails };
      }
      return null;
    };
  }

  public createAsyncValidator(
    condition: any,
    errorKey: string,
    errorDetails: any = "error",
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (condition(control)) {
        return of({ [errorKey]: errorDetails });
      }
      return of(null);
    };
  }

  public setValidators(control: AbstractControl, validators: any) {
    control.setValidators(validators);
  }

  public clearValidators(control: AbstractControl) {
    control.clearValidators();
  }

  public setAsyncValidators(control: AbstractControl, validators: any) {
    control.setAsyncValidators(validators);
  }

  public clearAsyncValidators(control: AbstractControl) {
    control.clearAsyncValidators();
  }
}
